import * as React from "react";

export type ShotBookmarkTarget = {
  x: number;
  y: number;
  z: number;
  label?: string | null;
};

export type ShotBookmarkLabState = {
  target?: ShotBookmarkTarget | null;
  targets?: ShotBookmarkTarget[] | null;
  pathLabel?: string | null;
};

export type ShotBookmark = {
  id: string;
  packageId: string;
  rallyIndex: number;
  shotNumber: number;
  timeSec: number | null;
  playerId: number | null;
  playerName: string | null;
  description: string;
  createdAt: string;
  updatedAt: string;
  lab?: ShotBookmarkLabState | null;
};

export type ShotBookmarkDraft = {
  packageId: string;
  rallyIndex: number;
  shotNumber: number;
  timeSec?: number | null;
  playerId?: number | null;
  playerName?: string | null;
  description?: string;
  lab?: ShotBookmarkLabState | null;
};

const STORAGE_PREFIX = "pbv.shotBookmarks.v1";
const CHANGE_EVENT = "pbv-shot-bookmarks-changed";

function storageKey(packageId: string) {
  return `${STORAGE_PREFIX}:${packageId || "unknown"}`;
}

function makeBookmarkId(draft: Pick<ShotBookmarkDraft, "packageId" | "rallyIndex" | "shotNumber">) {
  return `${draft.packageId}:r${draft.rallyIndex}:s${draft.shotNumber}`;
}

function notifyBookmarksChanged(packageId: string) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { packageId } }));
}

function normalizeBookmarks(value: unknown, packageId: string): ShotBookmark[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item): ShotBookmark | null => {
      if (!item || typeof item !== "object") return null;
      const raw = item as Partial<ShotBookmark>;
      const rallyIndex = Number(raw.rallyIndex);
      const shotNumber = Number(raw.shotNumber);
      if (!Number.isFinite(rallyIndex) || !Number.isFinite(shotNumber)) return null;
      const id =
        typeof raw.id === "string" && raw.id
          ? raw.id
          : makeBookmarkId({ packageId, rallyIndex, shotNumber });
      const now = new Date().toISOString();
      const rawLab = raw.lab && typeof raw.lab === "object" ? raw.lab : null;
      const labTargets = Array.isArray(rawLab?.targets)
        ? rawLab.targets
            .map((target): ShotBookmarkTarget | null => {
              if (!target || typeof target !== "object") return null;
              const t = target as Partial<ShotBookmarkTarget>;
              const x = Number(t.x);
              const y = Number(t.y);
              const z = Number(t.z);
              if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
                return null;
              }
              return {
                x,
                y,
                z,
                label: typeof t.label === "string" ? t.label : null,
              };
            })
            .filter((target): target is ShotBookmarkTarget => target != null)
        : null;
      const labTarget =
        rawLab?.target && typeof rawLab.target === "object"
          ? (() => {
              const t = rawLab.target as Partial<ShotBookmarkTarget>;
              const x = Number(t.x);
              const y = Number(t.y);
              const z = Number(t.z);
              if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
                return null;
              }
              return {
                x,
                y,
                z,
                label: typeof t.label === "string" ? t.label : null,
              };
            })()
          : null;
      const normalizedLab =
        rawLab == null
          ? null
          : {
              target: labTarget,
              targets:
                labTargets && labTargets.length
                  ? labTargets
                  : labTarget
                    ? [labTarget]
                    : null,
              pathLabel:
                typeof rawLab.pathLabel === "string" ? rawLab.pathLabel : null,
            };
      return {
        id,
        packageId,
        rallyIndex,
        shotNumber,
        timeSec: Number.isFinite(Number(raw.timeSec)) ? Number(raw.timeSec) : null,
        playerId: Number.isFinite(Number(raw.playerId)) ? Number(raw.playerId) : null,
        playerName: typeof raw.playerName === "string" ? raw.playerName : null,
        description: typeof raw.description === "string" ? raw.description : "",
        createdAt: typeof raw.createdAt === "string" ? raw.createdAt : now,
        updatedAt: typeof raw.updatedAt === "string" ? raw.updatedAt : now,
        lab: normalizedLab,
      };
    })
    .filter((bookmark): bookmark is ShotBookmark => bookmark != null);
}

export function loadShotBookmarks(packageId: string): ShotBookmark[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(storageKey(packageId));
    if (!raw) return [];
    return normalizeBookmarks(JSON.parse(raw), packageId);
  } catch {
    return [];
  }
}

export function saveShotBookmarks(packageId: string, bookmarks: ShotBookmark[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(storageKey(packageId), JSON.stringify(bookmarks));
  notifyBookmarksChanged(packageId);
}

export function upsertShotBookmark(draft: ShotBookmarkDraft): ShotBookmark {
  const bookmarks = loadShotBookmarks(draft.packageId);
  const now = new Date().toISOString();
  const id = makeBookmarkId(draft);
  const existing = bookmarks.find((bookmark) => bookmark.id === id);
  const next: ShotBookmark = {
    id,
    packageId: draft.packageId,
    rallyIndex: draft.rallyIndex,
    shotNumber: draft.shotNumber,
    timeSec: draft.timeSec ?? existing?.timeSec ?? null,
    playerId: draft.playerId ?? existing?.playerId ?? null,
    playerName: draft.playerName ?? existing?.playerName ?? null,
    description: draft.description ?? existing?.description ?? "",
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
    lab: draft.lab === undefined ? existing?.lab ?? null : draft.lab,
  };
  const merged = existing
    ? bookmarks.map((bookmark) => (bookmark.id === id ? next : bookmark))
    : [next, ...bookmarks];
  saveShotBookmarks(draft.packageId, merged);
  return next;
}

export function updateShotBookmarkDescription(
  packageId: string,
  bookmarkId: string,
  description: string,
) {
  const bookmarks = loadShotBookmarks(packageId);
  const now = new Date().toISOString();
  saveShotBookmarks(
    packageId,
    bookmarks.map((bookmark) =>
      bookmark.id === bookmarkId
        ? { ...bookmark, description, updatedAt: now }
        : bookmark,
    ),
  );
}

export function deleteShotBookmark(packageId: string, bookmarkId: string) {
  saveShotBookmarks(
    packageId,
    loadShotBookmarks(packageId).filter((bookmark) => bookmark.id !== bookmarkId),
  );
}

export function useShotBookmarks(packageId: string) {
  const [bookmarks, setBookmarks] = React.useState<ShotBookmark[]>(() =>
    loadShotBookmarks(packageId),
  );

  const refresh = React.useCallback(() => {
    setBookmarks(loadShotBookmarks(packageId));
  }, [packageId]);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  React.useEffect(() => {
    function onChanged(event: Event) {
      const detail = (event as CustomEvent<{ packageId?: string }>).detail;
      if (!detail?.packageId || detail.packageId === packageId) refresh();
    }
    window.addEventListener(CHANGE_EVENT, onChanged);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChanged);
      window.removeEventListener("storage", refresh);
    };
  }, [packageId, refresh]);

  return { bookmarks, refresh };
}
